import { VideoContext } from "../Player.js";

type WTOListener = () => void;


export enum WhenTimeOccursPolicy {
    ALWAYS = 0,
    ON_PLAYING = 1
}
/**
 * Manages the execution of listeners when a specific playback time occurs in a video context.
 *
 * The `WhenTimeOccurs` class allows registering callbacks to be invoked when the video playback
 * reaches or passes a specified time. It listens to time updates from the provided `VideoContext`
 * and triggers registered listeners according to the configured tick policy.
 *
 * @remarks
 * - Listeners are triggered only once when their target time is reached or passed.
 * - The tick policy determines whether listeners are checked on every time update or only when the video is playing.
 *
 * @example
 * ```typescript
 * const wto = new WhenTimeOccurs(videoContext, WhenTimeOccursPolicy.ALWAYS);
 * wto.whenTimeIsAfter(10, () => { console.log("Reached 10 seconds!"); });
 * ```
 *
 * @param videoContext - The video context to observe for time updates.
 * @param tickPolicy - The policy that determines when listeners should be checked and triggered.
 *
 * @public
 */
export class WhenTimeOccurs {

    private lastTime: number;
    private remaining: number[]; 
    private conditionMap: Map<number,WTOListener[]>;

    private _tickListener: () => void;

    constructor(private videoContext: VideoContext, private tickPolicy: WhenTimeOccursPolicy) {
        this.lastTime = 0;
        this.conditionMap = new Map<number,WTOListener[]>();
        this.remaining = [];
        const tickl = () => this.tick();
        this._tickListener = tickl;
        videoContext.on("timeupdate",tickl);
    }



    private tick() {
        if(this.tickPolicy == WhenTimeOccursPolicy.ALWAYS || (this.tickPolicy == WhenTimeOccursPolicy.ON_PLAYING && !this.videoContext.isPaused)) {
            const time = Math.floor(this.videoContext.currentTime);
            if(time > this.lastTime  && (time - this.lastTime) >= 1) {
                const [toProcess,newRemaining] =  predicateSplitter(this.remaining,(a) => a <= time);
                this.remaining = newRemaining;
                toProcess.forEach(k => {
                    const listeners = this.conditionMap.get(k)
                    listeners.forEach(lst => lst());
                    this.conditionMap.delete(k);
                })
                
            }
        }
    }

    public whenTimeIsAfter(time: number, listener: () => void) {
        let key = Math.floor(time);
        key = Math.max(key,0);
        let existing = this.conditionMap.get(key);

        if(!existing) {
           existing = [];
           this.conditionMap.set(key,existing); 
           this.remaining.push(key);
           this.remaining = this.remaining.sort((a,b) => a - b);
        }
        existing.push(listener);
    }   


    public destroy() {
        this.videoContext.off("timeupdate",this._tickListener);
    }
}


function predicateSplitter<A>(arr: A[], pred: (a: A) => boolean): [A[],A[]] {

    let i = 0;
    if(arr.length > 0) {
        while(pred(arr[i])) { i++; }
    }
    if(i <= 0) {
        return [[],arr];
    }
    else {
        return [arr.slice(0,i),arr.slice(i)];
    }

}